import { User as Auth0User } from '@auth0/auth0-react';
import {
  API_ROLES_WITH_SCOPES,
  type IApiRole,
  type IApiScope,
} from '@styluxinc/api-schema';
import { IResource, RESOURCE_TO_ROLE_MAP, SUPER_ADMIN_ROLES } from './roles';

export class User {
  name: string | null = null;
  _userProfile: Auth0User | null = null;
  accessToken: null | string = null;
  roleSet = new Set<IApiRole>();
  scopeSet = new Set<IApiScope>();
  viewAsRoles = new Set<IApiRole>();
  isSuperAdmin = false;

  constructor({
    name = null,
    userProfile = null,
  }: {
    accessToken?: string | null;
    name?: string | null;
    userProfile?: Auth0User | null;
  } = {}) {
    this.name = name;
    this.userProfile = userProfile;
  }

  set userProfile(userProfile: Auth0User | null) {
    this._userProfile = userProfile;

    const maybeRoles = userProfile
      ? (userProfile['https://api.stylux.io/roles'] as IApiRole[])
      : [];

    const roles = Array.isArray(maybeRoles) ? maybeRoles : [];
    this.roleSet = new Set(roles);

    const scopesFromRoles = roles.reduce<IApiScope[]>((acc, role) => {
      const scopesForRole = API_ROLES_WITH_SCOPES[role] || [];
      acc.push(...scopesForRole);
      return acc;
    }, []);

    this.scopeSet = new Set(scopesFromRoles);

    this.isSuperAdmin = this.hasRole(SUPER_ADMIN_ROLES);
    this.viewAsRoles = new Set();
  }

  get userProfile() {
    return this._userProfile;
  }

  viewAs(roles: IApiRole[]) {
    if (!this.isSuperAdmin) {
      console.warn('Only super admins can use view-as functionality');
      return;
    }
    this.viewAsRoles = new Set(roles);
  }

  hasRole(roleOrRoles: IApiRole | IApiRole[]) {
    const roleArr = Array.isArray(roleOrRoles) ? roleOrRoles : [roleOrRoles];
    if (this.viewAsRoles.size > 0) {
      return roleArr.some((role) => this.viewAsRoles.has(role));
    }

    return roleArr.some((role) => this.roleSet.has(role));
  }

  hasScope(scopeOrScopes: IApiScope | IApiScope[]) {
    const scopeArr = Array.isArray(scopeOrScopes)
      ? scopeOrScopes
      : [scopeOrScopes];
    return scopeArr.some((scope) => this.scopeSet.has(scope));
  }

  hasAccessTo(resource: IResource) {
    const requiredRoles = RESOURCE_TO_ROLE_MAP[resource];
    return this.hasRole(requiredRoles);
  }
}
